import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCamera,
  faTimes,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../config/constants";
import ImageCropper from "./ImageCropper";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

const QuickSubmitButton = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isOcrProcessing, setIsOcrProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [recognizedPlate, setRecognizedPlate] = useState("");
  const [showPlateInput, setShowPlateInput] = useState(false);
  const fileInputRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Track if we've moved during drag
  const hasMoved = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });

  // State for button position
  const [position, setPosition] = useState({ bottom: 24, right: 24 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Load saved position from localStorage on mount
  useEffect(() => {
    const savedPosition = localStorage.getItem("quickSubmitButtonPosition");
    if (savedPosition) {
      try {
        setPosition(JSON.parse(savedPosition));
      } catch (e) {
        console.error("Failed to parse saved position", e);
      }
    }
  }, []);

  // Save position to localStorage when it changes
  useEffect(() => {
    if (position.bottom !== 24 || position.right !== 24) {
      localStorage.setItem(
        "quickSubmitButtonPosition",
        JSON.stringify(position)
      );
    }
  }, [position]);

  const handleButtonClick = (e) => {
    // Only trigger click if we haven't moved during the drag
    if (isDragging && hasMoved.current) {
      return;
    }

    if (!user) {
      toast.error(t("common.login_required"));
      return;
    }
    fileInputRef.current.click();
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedFile(null);
    setShowCropper(false);
    setRecognizedPlate("");
    setShowPlateInput(false);
    setIsOcrProcessing(false);
    setIsSubmitting(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error(t("quickSubmit.image_only"));
      return;
    }

    setIsOpen(true);
    setSelectedFile(file);
    setShowPlateInput(true); // Show input immediately
    setRecognizedPlate(""); // Reset plate number
    setIsOcrProcessing(true); // Set OCR processing state for animation

    // Start OCR in background
    const formData = new FormData();
    formData.append("file", file);

    fetch(API_ENDPOINTS.OCR_PLATE, {
      method: "POST",
      credentials: "include",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.plate) {
          setRecognizedPlate(data.plate);
        }
      })
      .catch((err) => {
        console.error("Error recognizing plate:", err);
      })
      .finally(() => {
        setIsOcrProcessing(false); // Clear OCR processing state when done
      });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!recognizedPlate.trim()) {
      toast.error(t("quickSubmit.plate_required"));
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("plate", recognizedPlate.trim().toUpperCase());
      formData.append("skipCrop", !showCropper);

      const response = await fetch(API_ENDPOINTS.QUICK_SUBMIT_PLATE, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit plate");
      }

      const data = await response.json();
      toast.success(t("quickSubmit.success"));
      handleClose();
      navigate(`/plate/${data.plate}`);
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error.message || t("quickSubmit.error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCropComplete = (croppedFile) => {
    setSelectedFile(croppedFile);
    setShowCropper(false);
  };

  const handleCropCancel = () => {
    setShowCropper(false);
  };

  const handleCropClick = () => {
    setShowCropper(true);
  };

  // Drag handlers
  const handleMouseDown = (e) => {
    if (isOpen) return; // Don't allow dragging when modal is open

    // Prevent default behavior to avoid text selection
    e.preventDefault();

    // Reset movement tracking
    hasMoved.current = false;
    startPos.current = { x: e.clientX, y: e.clientY };

    // Set a flag to indicate we're starting to drag
    setIsDragging(true);

    // Calculate the offset from the pointer to the button edges
    const rect = buttonRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleTouchStart = (e) => {
    if (isOpen) return; // Don't allow dragging when modal is open

    // Prevent default behavior to avoid scrolling
    e.preventDefault();

    const touch = e.touches[0];

    // Reset movement tracking
    hasMoved.current = false;
    startPos.current = { x: touch.clientX, y: touch.clientY };

    // Set a flag to indicate we're starting to drag
    setIsDragging(true);

    // Calculate the offset from the touch point to the button edges
    const rect = buttonRef.current.getBoundingClientRect();
    setDragOffset({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    // Check if we've moved more than a small threshold
    const moveX = Math.abs(e.clientX - startPos.current.x);
    const moveY = Math.abs(e.clientY - startPos.current.y);

    if (moveX > 5 || moveY > 5) {
      hasMoved.current = true;
    }

    // Calculate new position based on viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const buttonWidth = buttonRef.current.offsetWidth;
    const buttonHeight = buttonRef.current.offsetHeight;

    // Calculate the position from the right and bottom edges
    const right = viewportWidth - (e.clientX - dragOffset.x + buttonWidth);
    const bottom = viewportHeight - (e.clientY - dragOffset.y + buttonHeight);

    // Ensure the button stays within viewport bounds
    const boundedRight = Math.max(
      0,
      Math.min(right, viewportWidth - buttonWidth)
    );
    const boundedBottom = Math.max(
      0,
      Math.min(bottom, viewportHeight - buttonHeight)
    );

    // Update position
    setPosition({
      right: boundedRight,
      bottom: boundedBottom,
    });
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;

    const touch = e.touches[0];

    // Check if we've moved more than a small threshold
    const moveX = Math.abs(touch.clientX - startPos.current.x);
    const moveY = Math.abs(touch.clientY - startPos.current.y);

    if (moveX > 5 || moveY > 5) {
      hasMoved.current = true;
    }

    // Calculate new position based on viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const buttonWidth = buttonRef.current.offsetWidth;
    const buttonHeight = buttonRef.current.offsetHeight;

    // Calculate the position from the right and bottom edges
    const right = viewportWidth - (touch.clientX - dragOffset.x + buttonWidth);
    const bottom =
      viewportHeight - (touch.clientY - dragOffset.y + buttonHeight);

    // Ensure the button stays within viewport bounds
    const boundedRight = Math.max(
      0,
      Math.min(right, viewportWidth - buttonWidth)
    );
    const boundedBottom = Math.max(
      0,
      Math.min(bottom, viewportHeight - buttonHeight)
    );

    // Update position
    setPosition({
      right: boundedRight,
      bottom: boundedBottom,
    });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Add event listeners for drag
  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleDragEnd);
      window.addEventListener("touchmove", handleTouchMove, { passive: false });
      window.addEventListener("touchend", handleDragEnd);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleDragEnd);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleDragEnd);
    };
  }, [isDragging]);

  if (!user) return null;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        className="fixed bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-full w-14 h-14 flex items-center justify-center shadow-lg hover:from-primary-700 hover:to-primary-800 active:from-primary-800 active:to-primary-900 transition-all duration-300 z-50 cursor-move dark:bg-primary-600 dark:hover:bg-primary-700 dark:active:bg-primary-800 dark:active:to-primary-900"
        aria-label={t("quickSubmit.button_tooltip")}
        style={{
          bottom: `${position.bottom}px`,
          right: `${position.right}px`,
          touchAction: "none", // Prevents default touch actions
        }}
      >
        <FontAwesomeIcon
          icon={faCamera}
          className="text-xl dark:text-gray-100"
        />
      </button>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/*"
        className="hidden"
      />

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4 dark:bg-gray-800 dark:text-gray-400">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold dark:text-gray-100">
                {t("quickSubmit.title")}
              </h3>
              <button
                onClick={handleClose}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {showCropper && selectedFile ? (
              <ImageCropper
                imageFile={selectedFile}
                onCropComplete={handleCropComplete}
                onCancel={handleCropCancel}
              />
            ) : showPlateInput ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                    {t("quickSubmit.preview")}
                  </label>
                  <div className="border rounded-lg overflow-hidden relative h-60">
                    <img
                      src={URL.createObjectURL(selectedFile)}
                      alt="Preview"
                      className="w-full h-full object-cover object-center"
                    />
                    {isOcrProcessing && (
                      <div className="absolute inset-0 overflow-hidden">
                        <div className="absolute inset-[-50%] bg-gradient-to-b from-transparent via-blue-500/40 to-transparent animate-scan"></div>
                        <div className="absolute inset-[-50%] bg-gradient-to-r from-transparent via-white/80 to-transparent animate-scan-horizontal"></div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="plate-input"
                    className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300"
                  >
                    {t("quickSubmit.plate_number")}
                  </label>
                  <input
                    id="plate-input"
                    type="text"
                    value={recognizedPlate}
                    onChange={(e) => setRecognizedPlate(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    placeholder={t("quickSubmit.plate_placeholder")}
                    autoFocus
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleCropClick}
                    className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  >
                    {t("quickSubmit.crop")}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 py-2 rounded-md hover:from-primary-700 hover:to-primary-800 active:from-primary-800 active:to-primary-900 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center dark:bg-primary-600 dark:hover:bg-primary-700 dark:active:bg-primary-800 dark:active:to-primary-900"
                  >
                    {isSubmitting ? (
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin mr-2"
                      />
                    ) : null}
                    {t("quickSubmit.submit")}
                  </button>
                </div>
              </form>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
};

export default QuickSubmitButton;
